import {
  createContext, useCallback, useContext, useRef, useState, type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  uploadMedia, updateMedia, uploadAudio, updateAudio,
  createMediaFromPdf, createFolder, assignFolder,
} from '@meno/ui/lib/api'
import { useTaskCenter } from '@/contexts/TaskCenterContext'
import {
  UploadConfirmDialog, type UploadPreview, type PdfUploadMode,
} from '@/pages/management/media-page/dialogs'
import {
  captureVideoThumbnail, readAudioDuration, THUMB_CACHE_PREFIX,
  uploadLimitError,
  type MediaFile, type AudioFile,
} from '@/pages/management/media-page/utils'

// ── Universal upload ─────────────────────────────────────────────────────
// One provider owns the single Assets-style upload experience for the whole
// signage app: the OS file picker, per-file staging (video thumbnail / gif
// preview / audio duration probe), and the shared UploadConfirmDialog which
// morphs per file type (image crop, PDF document/folder, video→audio, audio
// preview). Every "upload" entry point in the app calls `openUpload(...)`
// instead of rolling its own hidden input + crop modal, so the modal is
// genuinely universal and batches are supported (a multi-file selection is
// processed one confirm at a time through the same modal).

export type UploadedAsset =
  | { kind: 'media'; file: MediaFile }
  | { kind: 'audio'; audio: AudioFile }

export type OpenUploadOpts = {
  /** OS file-picker `accept` filter. Defaults to images/videos/PDF (+ audio
   *  when `allowAudio`). */
  accept?: string
  /** Allow multi-file selection → processed as a batch. Default true. */
  multiple?: boolean
  /** Include audio in the default accept filter. */
  allowAudio?: boolean
  /** Called once per successfully-uploaded asset (media or audio). */
  onUploaded?: (asset: UploadedAsset) => void
  /** Called after the whole batch drains, if at least one upload succeeded. */
  onComplete?: () => void
}

type UploadContextValue = {
  /** Opens the OS picker, then drives the shared modal over the selection. */
  openUpload: (opts?: OpenUploadOpts) => void
}

const UploadContext = createContext<UploadContextValue | null>(null)

const MEDIA_ACCEPT = 'image/*,video/mp4,video/webm,video/quicktime,application/pdf'
const MEDIA_AUDIO_ACCEPT = `${MEDIA_ACCEPT},audio/*`

// Stage a file for the modal: capture a video's poster frame + length, or read
// a gif as a data URL so it can animate in the preview. Everything else is
// previewed straight from the File inside the modal.
async function stageFile(file: File): Promise<UploadPreview> {
  if (file.type.startsWith('video')) {
    const { preview, duration } = await captureVideoThumbnail(file)
    return { file, preview, duration }
  }
  if (file.type === 'image/gif') {
    const preview = await new Promise<string | null>(resolve => {
      const reader = new FileReader()
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(file)
    })
    return { file, preview }
  }
  return { file, preview: null }
}

export function UploadProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { runUpload } = useTaskCenter()

  const inputRef = useRef<HTMLInputElement>(null)
  const optsRef = useRef<OpenUploadOpts>({})
  const queueRef = useRef<File[]>([])
  const successRef = useRef(0)
  // Uploads still running in the background after their modal has closed.
  const pendingRef = useRef(0)
  // `step` forces a fresh modal instance per file so its internal state (name,
  // pdf mode, video→audio toggle) resets between queued files.
  const [step, setStep] = useState(0)
  const [current, setCurrent] = useState<UploadPreview | null>(null)

  // Fire onComplete once the queue is drained AND no upload is still in flight,
  // so a caller's refresh (e.g. reloadMedia) observes every uploaded asset.
  const maybeComplete = useCallback(() => {
    if (queueRef.current.length > 0 || pendingRef.current > 0) return
    if (successRef.current > 0) optsRef.current.onComplete?.()
    successRef.current = 0
  }, [])

  // Stage + show the next queued file, or close the modal when the queue is
  // drained. Files that fail to stage (e.g. an unreadable video) are skipped.
  const advance = useCallback(async () => {
    while (queueRef.current.length > 0) {
      const next = queueRef.current[0]
      try {
        const staged = await stageFile(next)
        setStep(s => s + 1)
        setCurrent(staged)
        return
      } catch {
        queueRef.current = queueRef.current.slice(1)
      }
    }
    setCurrent(null)
    maybeComplete()
  }, [maybeComplete])

  const openUpload = useCallback((opts: OpenUploadOpts = {}) => {
    optsRef.current = opts
    const input = inputRef.current
    if (!input) return
    input.value = ''
    input.multiple = opts.multiple !== false
    input.accept = opts.accept ?? (opts.allowAudio ? MEDIA_AUDIO_ACCEPT : MEDIA_ACCEPT)
    input.click()
  }, [])

  const onFilesPicked = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return
    const accepted: File[] = []
    for (const file of Array.from(files)) {
      const error = uploadLimitError(file)
      if (error) toast.error(error)
      else accepted.push(file)
    }
    queueRef.current = accepted
    successRef.current = 0
    advance()
  }, [advance])

  // Perform the actual upload for the confirmed file, branching on type exactly
  // like the Assets page: PDF (document or folder-of-images), video→audio,
  // direct audio (with a duration probe), or plain media.
  const runOne = useCallback(async (
    result: { name: string; file: File; pdfMode?: PdfUploadMode; asAudio?: boolean },
    staged: UploadPreview,
  ): Promise<void> => {
    const opts = optsRef.current
    const file = result.file
    const finalName = result.name.trim() || file.name
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name)

    if (isPdf && result.pdfMode === 'folder') {
      // Rasterize each page → images filed under a new folder named after the PDF.
      const { data: pages } = await createMediaFromPdf(file, finalName)
      const base = finalName || file.name.replace(/\.pdf$/i, '') || 'PDF'
      const { data: folder } = await createFolder({ name: base })
      await assignFolder({ folder_id: folder.id, items: (pages as MediaFile[]).map(p => ({ kind: 'm' as const, item_id: p.id })) })
      ;(pages as MediaFile[]).forEach(p => opts.onUploaded?.({ kind: 'media', file: p }))
      toast.success(t('assets.pdfFoldered', { defaultValue: 'PDF added as a folder of {{count}} images.', count: (pages as MediaFile[]).length }))
      return
    }

    if (result.asAudio || file.type.startsWith('audio')) {
      // Video→audio import, or a plain audio file. Probe the length client-side
      // (the backend doesn't decode audio) in parallel with the upload.
      const durationP = file.type.startsWith('audio') ? readAudioDuration(file).catch(() => 0) : Promise.resolve(0)
      const { data } = await runUpload({ label: file.name, kind: 'audio', upload: (onProgress) => uploadAudio(file, onProgress) })
      const patch: Record<string, unknown> = {}
      if (finalName !== data.original_name) patch.original_name = finalName
      const seconds = await durationP
      if (seconds > 0) patch.duration = seconds
      let audio = data
      if (Object.keys(patch).length > 0) {
        try { const { data: updated } = await updateAudio(data.id, patch as any); audio = updated }
        catch { audio = { ...data, ...(patch as object) } }
      }
      opts.onUploaded?.({ kind: 'audio', audio })
      toast.success(t('assets.audioUploaded', 'Audio uploaded'))
      return
    }

    // Plain media (image already cropped by the modal, video, gif, or a
    // single-file PDF document).
    const { data } = await runUpload({ label: file.name, kind: 'media', upload: (onProgress) => uploadMedia(file, onProgress) })
    const patch: Record<string, unknown> = {}
    if (finalName !== data.original_name) patch.original_name = finalName
    if (file.type.startsWith('video') && staged.duration && staged.duration > 0) patch.duration = Math.round(staged.duration)
    if (Object.keys(patch).length > 0) {
      await updateMedia(data.id, patch as any, { initial: true })
      Object.assign(data, patch)
    }
    if (file.type.startsWith('video') && staged.preview) {
      try { localStorage.setItem(`${THUMB_CACHE_PREFIX}${data.id}`, staged.preview) } catch { /* quota — ignore */ }
    }
    opts.onUploaded?.({ kind: 'media', file: data })
    toast.success(isPdf ? t('assets.pdfUploaded', 'PDF uploaded.') : t('common.uploadSuccess', 'File uploaded successfully'))
  }, [runUpload, t])

  const handleConfirm = useCallback((result: { name: string; file: File; pdfMode?: PdfUploadMode; asAudio?: boolean }) => {
    const staged = current
    if (!staged) return
    // Drop this file from the queue and advance the modal right away — the
    // upload runs in the background (tracked by the Task Center), so the modal
    // and its task tile are never on screen at the same time.
    queueRef.current = queueRef.current.slice(1)
    pendingRef.current += 1
    void (async () => {
      try { await runOne(result, staged); successRef.current += 1 }
      catch (err: any) { toast.error(err?.response?.data?.detail || err?.message || t('common.uploadFailed', 'Upload failed')) }
      finally { pendingRef.current -= 1; maybeComplete() }
    })()
    advance()
  }, [current, runOne, advance, maybeComplete, t])

  // Cancel aborts the remaining queue (mirrors the single-file modal's "don't
  // upload"); uploads already sent keep running in the background.
  const handleCancel = useCallback(() => {
    queueRef.current = []
    setCurrent(null)
    maybeComplete()
  }, [maybeComplete])

  return (
    <UploadContext.Provider value={{ openUpload }}>
      {children}
      <input ref={inputRef} type="file" hidden onChange={e => onFilesPicked(e.target.files)} />
      {current && (
        <UploadConfirmDialog
          key={step}
          preview={current}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </UploadContext.Provider>
  )
}

export function useUpload(): UploadContextValue {
  const ctx = useContext(UploadContext)
  if (!ctx) throw new Error('useUpload must be used within an UploadProvider')
  return ctx
}
